/**
 * Country Name to ISO Code Mapping
 * Maps country names to ISO 3166-1 alpha-2 codes for API calls
 */
export const countryIsoMap: Record<string, string> = {
  // Top Destinations
  'United States': 'US',
  'United Kingdom': 'GB',
  'Great Britain': 'GB',
  'Japan': 'JP',
  'France': 'FR',
  'Germany': 'DE',
  'Canada': 'CA',
  'Australia': 'AU',
  'Singapore': 'SG',
  
  // Europe
  'Italy': 'IT',
  'Spain': 'ES',
  'Netherlands': 'NL',
  'Belgium': 'BE',
  'Switzerland': 'CH',
  'Austria': 'AT',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Denmark': 'DK',
  'Finland': 'FI',
  'Poland': 'PL',
  'Portugal': 'PT',
  'Greece': 'GR',
  'Ireland': 'IE',
  'Croatia': 'HR',
  'Czech Republic': 'CZ',
  'Hungary': 'HU',
  'Romania': 'RO',
  'Bulgaria': 'BG',
  
  // Asia
  'China': 'CN',
  'India': 'IN',
  'South Korea': 'KR',
  'Thailand': 'TH',
  'Malaysia': 'MY',
  'Indonesia': 'ID',
  'Philippines': 'PH',
  'Vietnam': 'VN',
  'Taiwan': 'TW',
  'Hong Kong': 'HK',
  
  // Middle East
  'United Arab Emirates': 'AE',
  UAE: 'AE',
  'Saudi Arabia': 'SA',
  'Turkey': 'TR',
  'Israel': 'IL',
  'Qatar': 'QA',
  'Kuwait': 'KW',
  'Oman': 'OM',
  
  // Americas
  'Mexico': 'MX',
  'Brazil': 'BR',
  'Argentina': 'AR',
  'Chile': 'CL',
  'Colombia': 'CO',
  'Peru': 'PE',
  
  // Africa
  'South Africa': 'ZA',
  'Egypt': 'EG',
  'Morocco': 'MA',
  'Kenya': 'KE',
  'Nigeria': 'NG',
  
  // Oceania
  'New Zealand': 'NZ',
  
  // Additional countries from API
  'Aland Islands': 'AX',
  'Aaland Islands': 'AX', // Alternative spelling
  'Afghanistan': 'AF',
  'Albania': 'AL',
  'Andorra': 'AD',
  'Anguilla': 'AI',
  'Antigua and Barbuda': 'AG',
  'Armenia': 'AM',
  'Aruba': 'AW',
  'Azerbaijan': 'AZ',
  'Bahamas': 'BS',
  'Bahrain': 'BH',
  'Bangladesh': 'BD',
  'Barbados': 'BB',
  'Belarus': 'BY',
  'Benin': 'BJ',
  'Bermuda': 'BM',
  'Bolivia': 'BO',
  'Bonaire, Sint Eustatius And Saba': 'BQ',
  'Bosnia and Herzegovina': 'BA',
  'Botswana': 'BW',
  'Brunei Darussalam': 'BN',
  'Brunei': 'BN',
  'Burkina Faso': 'BF',
  'Cameroon': 'CM',
  'Cape Verde': 'CV',
  'Central African Republic': 'CF',
  'Congo': 'CG',
  'Congo, the Democratic Republic of the': 'CD',
  'Democratic Republic of the Congo': 'CD',
  'Costa Rica': 'CR',
  'Cote d\'Ivoire': 'CI',
  'Ivory Coast': 'CI',
  'Cuba': 'CU',
  'Curacao': 'CW',
  'Cyprus': 'CY',
  'Northern Cyprus': 'CYP',
  'Dominica': 'DM',
  
  // Add more as needed - check your backend for available country codes
}

/**
 * Get ISO code from country name
 */
export const getCountryIso = (countryName: string): string | null => {
  return countryIsoMap[countryName] || null
}

let cachedRegionNamesEn: Intl.DisplayNames | null | undefined

function getRegionNamesEn(): Intl.DisplayNames | null {
  if (cachedRegionNamesEn !== undefined) return cachedRegionNamesEn
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') {
    cachedRegionNamesEn = null
    return null
  }
  try {
    cachedRegionNamesEn = new Intl.DisplayNames(['en'], { type: 'region' })
  } catch {
    cachedRegionNamesEn = null
  }
  return cachedRegionNamesEn
}

/**
 * English display name for an ISO 3166-1 alpha-2 code (e.g. "DE" → "Germany").
 * Uses {@link Intl.DisplayNames}; returns null if unavailable or unknown.
 */
export function getCountryNameFromIso(iso: string): string | null {
  const code = iso.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(code)) return null
  const rn = getRegionNamesEn()
  if (!rn) return null
  try {
    const label = rn.of(code)
    if (!label || label === code) return null
    return label
  } catch {
    return null
  }
}

const DISPLAY_NAME_ALIASES: Record<string, string> = {
  UK: 'United Kingdom',
  UAE: 'United Arab Emirates',
  USA: 'United States',
}

function lookupNameFromIsoMap(iso2: string): string | null {
  const upper = iso2.toUpperCase()
  for (const [name, code] of Object.entries(countryIsoMap)) {
    if (code.toUpperCase() === upper && name.length > 2) {
      return name
    }
  }
  return null
}

/**
 * Normalize API / catalogue labels to a full English country name for UI.
 * Leaves real names unchanged; resolves 2-letter ISO codes (e.g. "AF" → "Afghanistan").
 */
export function resolveCountryDisplayName(countryName: string, countryIso?: string): string {
  const trimmed = countryName.trim()
  if (!trimmed) return trimmed

  const alias = DISPLAY_NAME_ALIASES[trimmed.toUpperCase()]
  if (alias) return alias

  const isoCandidate = (countryIso?.trim() || trimmed).toUpperCase()
  const iso2 = /^[A-Z]{2}$/.test(isoCandidate)
    ? isoCandidate
    : /^[A-Z]{2}$/.test(trimmed.toUpperCase())
      ? trimmed.toUpperCase()
      : null

  if (iso2 && (trimmed.length <= 3 || trimmed.toUpperCase() === iso2)) {
    return (
      lookupNameFromIsoMap(iso2) ||
      getCountryNameFromIso(iso2) ||
      trimmed
    )
  }

  return trimmed
}

